import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Check, ChevronsUpDown, LucideIcon, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface UniversalItem {
  id: string;
  name: string;
  [key: string]: any; // Allow additional properties
}

interface UniversalSelectorProps<T extends UniversalItem> {
  items: T[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  allowFreeText?: boolean;
  freeTextValue?: string;
  onFreeTextChange?: (value: string) => void;
  label?: string;
  error?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  onEnterPress?: () => void;
  icon?: LucideIcon;
  // Customization functions
  filterFunction?: (item: T, searchValue: string) => boolean;
  displayFunction?: (item: T) => React.ReactNode;
  badgeFunction?: (item: T) => string | null;
  required?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  // Quick add functionality
  canAddNew?: boolean;
  onAddNew?: (name: string) => Promise<void>;
  addNewLabel?: string;
}

export function UniversalSelector<T extends UniversalItem>({
  items,
  value,
  onValueChange,
  placeholder = "Selecione uma opção",
  allowFreeText = false,
  freeTextValue = "",
  onFreeTextChange,
  label = "Seleção",
  error,
  inputRef,
  onEnterPress,
  icon: Icon,
  filterFunction,
  displayFunction,
  badgeFunction,
  required = false,
  onFocus,
  onBlur,
  canAddNew = false,
  onAddNew,
  addNewLabel = "Adicionar Novo"
}: UniversalSelectorProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [showAddDialog, setShowAddDialog] = React.useState(false);
  const [newItemName, setNewItemName] = React.useState("");
  const [isAdding, setIsAdding] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const internalInputRef = React.useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Use external ref if provided, otherwise use internal ref
  const activeInputRef = inputRef || internalInputRef;

  // Default filter function
  const defaultFilterFunction = (item: T, searchValue: string): boolean => {
    return (item.name?.toLowerCase() || "").includes(searchValue.toLowerCase());
  };

  // Default display function - allows text wrapping for long names
  const defaultDisplayFunction = (item: T): React.ReactNode => {
    return <span className="font-medium break-words">{item.name}</span>;
  };

  const actualFilterFunction = filterFunction || defaultFilterFunction;
  const actualDisplayFunction = displayFunction || defaultDisplayFunction;

  // Fechar dropdown ao clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
        onBlur?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onBlur]);

  const selectedItem = items.find((item) => item.id === value);

  const filteredItems = items.filter((item) =>
    actualFilterFunction(item, searchValue)
  );

  const handleSelect = (itemId: string) => {
    onValueChange(itemId);
    setOpen(false);
    setSearchValue("");
    setSelectedIndex(-1);
  };

  // Filtrar itens baseado no texto digitado
  const filteredItemsByInput = items.filter((item) =>
    actualFilterFunction(item, freeTextValue || "")
  );

  // Função para navegar com teclado
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (!open || filteredItemsByInput.length === 0)) {
      e.preventDefault();
      onEnterPress?.();
      return;
    }

    if (!open || filteredItemsByInput.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredItemsByInput.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredItemsByInput.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredItemsByInput.length) {
          const selectedItem = filteredItemsByInput[selectedIndex];
          onFreeTextChange?.(selectedItem.name);
          setOpen(false);
          setSelectedIndex(-1);
          setTimeout(() => onEnterPress?.(), 100);
        } else {
          onEnterPress?.();
        }
        break;
      case 'Escape':
        setOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Reset do índice selecionado quando a lista muda
  React.useEffect(() => {
    setSelectedIndex(-1);
  }, [filteredItemsByInput.length, freeTextValue]);

  if (allowFreeText) {
    return (
      <div className="space-y-2">
        <Label className={cn(error && "text-destructive")}>
          {label} {required && "*"}
        </Label>
        <div className="relative" ref={dropdownRef}>
          <Input
            ref={activeInputRef}
            value={freeTextValue}
            onChange={(e) => {
              onFreeTextChange?.(e.target.value);
              // Filtrar itens baseado no novo valor
              const newFilteredItems = items.filter((item) =>
                actualFilterFunction(item, e.target.value)
              );
              // Abrir dropdown automaticamente quando começar a digitar ou quando há itens para mostrar
              if ((e.target.value.length > 0 && newFilteredItems.length > 0) || (e.target.value.length === 0 && items.length > 0)) {
                setOpen(true);
              }
            }}
            onFocus={() => {
              onFocus?.();
              // Abrir dropdown ao focar - sempre mostrar opções disponíveis
              if (items.length > 0) {
                setOpen(true);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(
              "pr-10",
              error && "border-destructive focus-visible:ring-destructive"
            )}
          />
          {Icon && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-muted"
              onClick={() => {
                setOpen(!open);
                if (!open) onFocus?.();
              }}
            >
              <Icon className="h-4 w-4" />
            </Button>
          )}
          
          {/* Dropdown de sugestões */}
          {open && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border rounded-md shadow-lg max-h-64 overflow-auto min-w-[400px]">
              <TooltipProvider delayDuration={300}>
                {(freeTextValue ? filteredItemsByInput : items).map((item, index) => (
                  <Tooltip key={item.id}>
                    <TooltipTrigger asChild>
                      <div
                        onClick={() => {
                          onFreeTextChange?.(item.name);
                          setOpen(false);
                          setSelectedIndex(-1);
                        }}
                        className={cn(
                          "flex items-center justify-between p-3 cursor-pointer border-b last:border-b-0",
                          index === selectedIndex ? "bg-accent text-accent-foreground" : "hover:bg-muted"
                        )}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {Icon && <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                          <div className="min-w-0 flex-1">
                            {actualDisplayFunction(item)}
                          </div>
                        </div>
                        {badgeFunction && badgeFunction(item) && (
                          <Badge variant="secondary" className="text-xs ml-2 flex-shrink-0">
                            {badgeFunction(item)}
                          </Badge>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-md">
                      <p className="break-words">{item.name}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              
              {/* Opção "Cadastrar Novo" sempre visível quando canAddNew é true */}
              {canAddNew && onAddNew && (
                <div
                  onClick={() => {
                    setShowAddDialog(true);
                    setNewItemName(freeTextValue?.trim() || "");
                    setOpen(false);
                  }}
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted border-t"
                >
                  <Plus className="h-4 w-4 text-primary" />
                  <span className="text-sm text-primary font-medium">
                    {freeTextValue?.trim() 
                      ? `${addNewLabel || "Cadastrar Novo"}: "${freeTextValue.trim()}"` 
                      : addNewLabel || "Cadastrar Novo"
                    }
                  </span>
                </div>
              )}
              </TooltipProvider>
            </div>
          )}
        </div>
        {error && (
          <div className="text-sm text-destructive">{error}</div>
        )}
      </div>
    );
  }

  const triggerInputRef = React.useRef<HTMLInputElement>(null);
  const dropdownListRef = React.useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const justSelectedRef = React.useRef(false);

  // Texto exibido no input - mostra nome selecionado ou busca
  const displayValue = open ? searchValue : (selectedItem?.name || "");

  // Reset do índice destacado quando filteredItems muda
  React.useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredItems.length, searchValue]);

  // Scroll para manter item destacado visível
  React.useEffect(() => {
    if (highlightedIndex >= 0 && dropdownListRef.current) {
      const items = dropdownListRef.current.querySelectorAll('[data-item-index]');
      const item = items[highlightedIndex] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleSelectItem = (itemId: string, advanceToNext: boolean = false) => {
    justSelectedRef.current = true;
    onValueChange(itemId);
    setOpen(false);
    setSearchValue("");
    setHighlightedIndex(-1);
    
    // Reset flag after a short delay
    setTimeout(() => {
      justSelectedRef.current = false;
    }, 100);
    
    if (advanceToNext) {
      setTimeout(() => onEnterPress?.(), 50);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setSearchValue("");
      setHighlightedIndex(-1);
    } else if (e.key === 'Tab') {
      // Se há item destacado, seleciona antes de avançar
      if (open && highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
        handleSelectItem(filteredItems[highlightedIndex].id);
      }
      setOpen(false);
      setSearchValue("");
      setHighlightedIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlightedIndex(prev => 
        prev < filteredItems.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlightedIndex(prev => 
        prev > 0 ? prev - 1 : filteredItems.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
        handleSelectItem(filteredItems[highlightedIndex].id, true);
      } else if (!open || filteredItems.length === 0) {
        onEnterPress?.();
      }
    }
  };

  return (
    <div className="space-y-2">
      <Label className={cn(error && "text-destructive")}>
        {label} {required && "*"}
      </Label>
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          {Icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          <Input
            ref={triggerInputRef}
            value={displayValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setHighlightedIndex(-1);
              if (!open) {
                setOpen(true);
              }
            }}
            onFocus={() => {
              // Não reabrir se acabou de selecionar
              if (!justSelectedRef.current) {
                setOpen(true);
              }
              onFocus?.();
            }}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            className={cn(
              "w-full h-10 bg-background pr-8",
              Icon && "pl-9",
              error && "border-destructive focus-visible:ring-destructive"
            )}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground opacity-50" />
          </div>
        </div>

        {/* Dropdown de opções */}
        {open && (
          <div 
            ref={dropdownListRef}
            className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border rounded-md shadow-lg max-h-64 overflow-auto min-w-[400px]"
          >
            <TooltipProvider delayDuration={300}>
              {filteredItems.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Nenhum item encontrado.
                </div>
              ) : (
                filteredItems.map((item, index) => (
                  <Tooltip key={item.id}>
                    <TooltipTrigger asChild>
                      <div
                        data-item-index={index}
                        onClick={() => {
                          handleSelectItem(item.id);
                          triggerInputRef.current?.focus();
                        }}
                        className={cn(
                          "flex items-center justify-between p-3 cursor-pointer border-b last:border-b-0",
                          index === highlightedIndex 
                            ? "bg-accent text-accent-foreground" 
                            : "hover:bg-muted"
                        )}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {Icon && <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                          <div className="min-w-0 flex-1">
                            {actualDisplayFunction(item)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {badgeFunction && badgeFunction(item) && (
                            <Badge variant="secondary" className="text-xs">
                              {badgeFunction(item)}
                            </Badge>
                          )}
                          <Check
                            className={cn(
                              "h-4 w-4",
                              value === item.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-md">
                      <p className="break-words">{item.name}</p>
                    </TooltipContent>
                  </Tooltip>
                ))
              )}

              {/* Opção para adicionar novo */}
              {canAddNew && onAddNew && (
                <div
                  onClick={() => {
                    setShowAddDialog(true);
                    setNewItemName(searchValue.trim());
                    setOpen(false);
                  }}
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted border-t"
                >
                  <Plus className="h-4 w-4 text-primary" />
                  <span className="text-sm text-primary font-medium">
                    {searchValue.trim() 
                      ? `${addNewLabel}: "${searchValue.trim()}"` 
                      : addNewLabel
                    }
                  </span>
                </div>
              )}
            </TooltipProvider>
          </div>
        )}
      </div>
      {error && (
        <div className="text-sm text-destructive">{error}</div>
      )}

      {/* Dialog para adicionar novo item */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{addNewLabel}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-item-name">Nome</Label>
              <Input
                id="new-item-name"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder={`Digite o nome`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddNew();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddDialog(false);
                  setNewItemName("");
                }}
                disabled={isAdding}
              >
                Cancelar
              </Button>
              <Button onClick={handleAddNew} disabled={isAdding || !newItemName.trim()}>
                {isAdding ? "Adicionando..." : "Adicionar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  async function handleAddNew() {
    if (!newItemName.trim() || !onAddNew) return;
    
    setIsAdding(true);
    try {
      await onAddNew(newItemName.trim());
      setShowAddDialog(false);
      setNewItemName("");
      toast({ title: "Item adicionado com sucesso!", duration: 1000 });
    } catch (error) {
      console.error("Error adding new item:", error);
      toast({ title: "Erro ao adicionar item", variant: "destructive", duration: 1000 });
    } finally {
      setIsAdding(false);
    }
  }
}